import {
  DeleteOutlined,
  OrderedListOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
} from "antd";
import momentTz from "moment-timezone";
import { useState } from "react";
import {
  ISortableProps,
  Sortable,
  SortItemModel,
} from "shared/components/sortable/sortable";
import { DataTypeEnum, DataTypeText } from "shared/constants/app-enums";
import {
  t,
  CoreResource,
  momentDateTimeFormat,
} from "shared/plugins/localization";
import { ProductAttributeGroupModel, ProductAttributeModel, ProductAttributeValueModel } from "states/product-attribute/product-attribute.models";
import {

} from "states/product/product.models";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "app.store";
import { ProductCategoryProductAttributeGroupModel, ProductCategoryProductAttributeModel, ProductCategoryProductAttributeValueModel } from "states/product-category/product-category.models";
import { ProductAttributeValueSelector } from "shared/components/product-attribute/product-attr-value-selector";
import { ProductAttributeGroupSelector } from "shared/components/product-attribute/product-attr-group-selector";
import { ProductAttributeSelector } from "shared/components/product-attribute/product-attr-selector";

interface IProductCategoryAttributeTabProps {
  attributeGroups:ProductCategoryProductAttributeGroupModel[];
  onUpdated: (attributeGroups:ProductCategoryProductAttributeGroupModel[]) => void;
}

export function ProductCategoryAttributeTab(props: IProductCategoryAttributeTabProps) {
  const {
    attributeGroups,
    onUpdated,
  } = props;

  const currentTimeZone = useSelector(
    (state: RootState) => state.globalValue.ianaTimeZone,
    shallowEqual
  );

  const [sortableModel, setSortableModel] = useState<ISortableProps>({
    model: [] as SortItemModel[],
    visible: false,
    onCancel: () => {
      setSortableModel({ ...sortableModel, visible: false });
    },
  } as ISortableProps);

  const handleAddAttributeGroup = () => {
    attributeGroups.push({
      sort: attributeGroups.length + 1,
      attributes: [] as ProductCategoryProductAttributeModel[],
    } as ProductCategoryProductAttributeGroupModel);
    onUpdated([...attributeGroups]);
  };

  const _handleSortAttributeGroup = () => {
    if (attributeGroups.filter((m) => !m.attributeGroupId).length > 0) {
      message.error("Vui lòng chọn nhóm thuộc tính");
      return;
    }
    sortableModel.model = attributeGroups.map((m) => {
      return {
        key: m.attributeGroupId.toString(),
        sort: m.sort,
        title: m.attributeGroupName,
      } as SortItemModel;
    });
    sortableModel.visible = true;
    sortableModel.title = "Sắp xếp thứ tự thuộc tính";
    sortableModel.onOk = async (result) => {
      const newList:ProductCategoryProductAttributeGroupModel[] = [];
      result.forEach((item) => {
        const attributegroup = attributeGroups.find(
          (m) => m.attributeGroupId.toString() === item.key
        );
        if (!attributegroup) return;
        newList.push(attributegroup);
      });
      onUpdated([...newList]);
      setSortableModel({ ...sortableModel, visible: false });
    };
    setSortableModel({ ...sortableModel });
  };

  const _handleSortAttribute = (
    attributeGroup:ProductCategoryProductAttributeGroupModel
  ) => {
    if (attributeGroup.attributes.filter((m) => !m.attributeId).length > 0) {
      message.error("Vui lòng chọn thuộc tính");
      return;
    }
    sortableModel.model = attributeGroup.attributes.map((m) => {
      return {
        key: m.attributeId?.toString(),
        sort: m.sort,
        title: m.attributeName,
      } as SortItemModel;
    });
    sortableModel.visible = true;
    sortableModel.title = attributeGroup.attributeGroupName;
    sortableModel.onOk = async (result) => {
      const newList:ProductCategoryProductAttributeModel[] = [];
      result.forEach((item) => {
        const attribute = attributeGroup.attributes.find(
          (m) => m.attributeId?.toString() === item.key
        );
        if (!attribute) return;
        newList.push(attribute);
      });
      attributeGroup.attributes = newList;
      onUpdated([...attributeGroups]);
      setSortableModel({ ...sortableModel, visible: false });
    };
    setSortableModel({ ...sortableModel });
  };

  const _renderAttributeValue = (attribute:ProductCategoryProductAttributeModel) => {
    switch (attribute.dataType) {
      case DataTypeEnum.ValueSelect: {
        return (
          <ProductAttributeValueSelector
            onChange={(values) => {
              let valuesTemp = values as ProductAttributeValueModel[];
              if (!valuesTemp) return;
              attribute.values = valuesTemp.map(
                (m) =>
                  ({
                    valueId: m.id,
                  } as ProductCategoryProductAttributeValueModel)
              );
              onUpdated([...attributeGroups]);
            }}
            isMultiple={true}
            value={attribute.values.map((m) => m.valueId)}
          />
        );
      }
      case DataTypeEnum.Boolean: {
        return (
          <Checkbox
            checked={attribute.valueBoolean}
            onChange={(e) => {
              attribute.valueBoolean = e.target.checked;
              onUpdated([...attributeGroups]);
            }}
          ></Checkbox>
        );
      }
      case DataTypeEnum.DateTime: {
        return (
          <DatePicker
            format={momentDateTimeFormat()}
            value={
              attribute.valueDate
                ? momentTz.tz(attribute.valueDate, currentTimeZone)
                : undefined
            }
            onChange={(e, dateString) => {
              attribute.valueDate = momentTz
                .tz(dateString, momentDateTimeFormat(), currentTimeZone)
                .toDate();
              onUpdated([...attributeGroups]);
            }}
            showTime
            showSecond={false}
            className="w-100"
          />
        );
      }
      case DataTypeEnum.Number: {
        return (
          <Input
            value={attribute.valueNumber}
            onChange={(e) => {
              attribute.valueNumber = e.target.valueAsNumber;
              onUpdated([...attributeGroups]);
            }}
            placeholder="Enter value"
            type="number"
          />
        );
      }
      case DataTypeEnum.String: {
        return (
          <Input
            value={attribute.valueNumber}
            onChange={(e) => {
              attribute.valueString = e.target.value;
              onUpdated([...attributeGroups]);
            }}
            placeholder="Enter value"
          />
        );
      }
    }
  };

  return (
    <>
      <Button
        type="primary"
        className="mb-4 mr-2"
        onClick={handleAddAttributeGroup}
        icon={<PlusCircleOutlined />}
      >
       Add attribute group
      </Button>
      <Button
        onClick={_handleSortAttributeGroup}
        type="default"
        icon={<OrderedListOutlined />}
      >
        {"Sắp xếp"}
      </Button>
      {attributeGroups.map((group, groupIndex) => (
        <Collapse expandIconPosition="right" className="mb-4">
          <Collapse.Panel
            key={group.attributeGroupId}
            header={
              <Space>
                <ProductAttributeGroupSelector
                  onChange={(value) => {
                    let valueTemp = value as ProductAttributeGroupModel;
                    if(!valueTemp) return;
                    group.attributeGroupId = valueTemp.id;
                    group.attributeGroupName = valueTemp.name;
                    onUpdated([...attributeGroups]);
                  }}
                  width={"200px"}
                  isMultiple={false}
                  value={group.attributeGroupId}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newAttribute = new ProductCategoryProductAttributeModel();
                    newAttribute.sort = group.attributes.length + 1;
                    group.attributes.push(newAttribute);
                    onUpdated([...attributeGroups]);
                  }}
                  type="primary"
                  icon={<PlusCircleOutlined />}
                />
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    _handleSortAttribute(group);
                  }}
                  type="default"
                  icon={<OrderedListOutlined />}
                ></Button>
                <Popconfirm
                  title={"Bạn có muốn xóa không ?"}
                  onConfirm={(e) => {
                    attributeGroups.splice(groupIndex, 1);
                    onUpdated([...attributeGroups]);
                  }}
                  okText={"Đồng ý"}
                  cancelText={"Hủy"}
                >
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </Space>
            }
          >
            {group.attributes.map((attribute, attributeIndex) => (
              <Row gutter={[16, 16]} className="mb-4">
                <Col span="6">
                <ProductAttributeSelector
                  onChange={(value) => {
                    let valueTemp = value as ProductAttributeModel;
                    if(!valueTemp) return;
                    attribute.attributeId = valueTemp.id;
                    attribute.attributeName = valueTemp.name;
                    onUpdated([...attributeGroups]);
                  }}
                  isMultiple={false}
                  value={attribute.attributeId}
                />
                </Col>
                <Col span="6">
                  <Select
                    className="w-100"
                    placeholder="Select data type"
                    allowClear
                    value={attribute.dataType}
                    showSearch
                    filterOption={(input, option: any) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(value) => {
                      attribute.dataType = value;
                      attribute.resetValue();
                      onUpdated([...attributeGroups]);
                    }}
                  >
                    {Object.keys(DataTypeEnum).map((key) =>
                      !isNaN(Number(key)) ? (
                        <Select.Option value={Number(key)}>
                          {DataTypeText[Number(key)]}
                        </Select.Option>
                      ) : null
                    )}
                  </Select>
                </Col>
                <Col span="12" className="d-flex">
                  <div className="flex-auto">
                    {_renderAttributeValue(attribute)}
                  </div>
                  <Popconfirm
                    title={t(CoreResource, "QuestionRemove")}
                    onConfirm={(e) => {
                      group.attributes.splice(groupIndex, 1);
                      onUpdated([...attributeGroups]);
                    }}
                    okText={"Đồng ý"}
                    cancelText={"Hủy"}
                    className="ml-2"
                  >
                    <Button danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Col>
              </Row>
            ))}
          </Collapse.Panel>
        </Collapse>
      ))}
      <Sortable
        title={sortableModel.title}
        model={sortableModel.model}
        visible={sortableModel.visible}
        onOk={sortableModel.onOk}
        onCancel={sortableModel.onCancel}
      />
    </>
  );
}
